/**
 * Модалка «Завести хозяйство» — шаг 4 флоу консультанта.
 *
 * Одно решение человека: в каком отделе/филиале живёт хозяйство клиента (это
 * кластер в структуре компании). Отдел предвыбран по данным CRM, но **меняется
 * руками** (решение founder 18.08) — предвыбор здесь подсказка, а не запрет.
 *
 * Данные из CRM (менеджер, ИНН, город) показаны блоком только для чтения:
 * правятся они в CRM, и дублировать их вводом значит завести второй источник
 * правды, который разойдётся с первым на второй неделе.
 *
 * Кабинет клиента здесь НЕ создаётся — он появится под первое поле (модель D).
 * Об этом сказано строкой в модалке, иначе «завёл, а ничего не появилось»
 * читается как сбой.
 */
import { useEffect, useMemo, useState } from 'react';
import { Check, ChevronDown, Search } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';

export interface DepartmentOption {
  id: string;
  name: string;
}

/**
 * Минимум, который модалке нужен о клиенте. Намеренно НЕ ``ConsultantClient``:
 * та же модалка открывается из карточки клиента, где под рукой запись CRM, а
 * не строка внешнего списка. Два входа — один путь (шаг 4 флоу).
 */
export interface FarmSetupClient {
  title: string | null;
  external_id?: string | null;
  inn?: string | null;
  city?: string | null;
  region?: string | null;
  assigned_by_name?: string | null;
  department_path?: string | null;
  farm_name?: string | null;
  cluster_id?: string | null;
}

/** Что модалка отдаёт наружу: имя хозяйства + отдел + «идём рисовать?». */
export interface FarmSetupResult {
  name: string;
  clusterId: string | null;
  addFields: boolean;
}

export interface FarmSetupDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  client: FarmSetupClient;
  departments: DepartmentOption[];
  onSubmit: (result: FarmSetupResult) => void | Promise<void>;
  submitting?: boolean;
  error?: string | null;
}

/** Регистр и «ё» не должны мешать сопоставлению — как и везде в поиске. */
function norm(value: string): string {
  return value.toLowerCase().replace(/ё/g, 'е').trim();
}

/**
 * Предвыбор отдела по данным CRM: последний узел пути отдела («Кристалл ›
 * Отдел Юг» → «Отдел Юг») сопоставляется с кластером по имени.
 *
 * Сопоставление ПО ИМЕНИ, а не по идентификатору, потому что общего ключа
 * между отделами внешней системы продаж и нашими кластерами не существует —
 * его никто не заводил. Отсюда же и мягкость поведения: не нашли совпадения —
 * просто не предвыбираем, а не подставляем «первый попавшийся отдел».
 * Человек всё равно может выбрать сам, и это дешевле, чем молча закрепить
 * клиента не за тем отделом.
 */
export function pickDepartmentCluster(
  departments: DepartmentOption[],
  departmentPath: string | null,
): string | null {
  if (!departmentPath) return null;
  const leaf = norm(departmentPath.split('›').pop() ?? '');
  if (!leaf) return null;
  const exact = departments.find((d) => norm(d.name) === leaf);
  if (exact) return exact.id;
  const partial = departments.find(
    (d) => norm(d.name).includes(leaf) || leaf.includes(norm(d.name)),
  );
  return partial ? partial.id : null;
}

export function FarmSetupDialog({
  open,
  onOpenChange,
  client,
  departments,
  onSubmit,
  submitting = false,
  error = null,
}: FarmSetupDialogProps) {
  const suggestedCluster = useMemo(
    () => client.cluster_id ?? pickDepartmentCluster(departments, client.department_path ?? null),
    [client.cluster_id, client.department_path, departments],
  );

  const [name, setName] = useState('');
  const [clusterId, setClusterId] = useState<string | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [query, setQuery] = useState('');

  // Пере-заполняем на КАЖДОЕ открытие: модалка живёт в списке и переиспользуется
  // для разных клиентов — оставшееся от прошлого клиента имя здесь худшая из
  // возможных ошибок.
  useEffect(() => {
    if (!open) return;
    setName(client.farm_name ?? client.title ?? '');
    setClusterId(suggestedCluster);
    setQuery('');
    setPickerOpen(false);
  }, [open, client.farm_name, client.title, suggestedCluster]);

  const selected = departments.find((d) => d.id === clusterId) ?? null;
  const filtered = useMemo(() => {
    const q = norm(query);
    if (!q) return departments;
    return departments.filter((d) => norm(d.name).includes(q));
  }, [departments, query]);

  const canSubmit = name.trim().length > 0 && !submitting;

  const submit = (addFields: boolean) => {
    if (!canSubmit) return;
    void onSubmit({ name: name.trim(), clusterId, addFields });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="farm-setup-dialog">
        <DialogHeader>
          <DialogTitle>Завести хозяйство</DialogTitle>
          <DialogDescription>
            для клиента {client.title ?? 'без названия'}
          </DialogDescription>
        </DialogHeader>

        {/* Из CRM — только для чтения */}
        <div className="rounded-md bg-muted/50 p-3 text-sm">
          <p className="mb-2 text-xs uppercase tracking-wide text-muted-foreground">
            Из CRM{client.external_id ? ` · запись ${client.external_id}` : ''}
          </p>
          <dl className="space-y-1">
            {client.assigned_by_name && (
              <div className="flex justify-between gap-3">
                <dt className="text-muted-foreground">Ответственный менеджер</dt>
                <dd className="font-medium">{client.assigned_by_name}</dd>
              </div>
            )}
            {client.inn && (
              <div className="flex justify-between gap-3">
                <dt className="text-muted-foreground">ИНН</dt>
                <dd className="font-medium">{client.inn}</dd>
              </div>
            )}
            {(client.city || client.region) && (
              <div className="flex justify-between gap-3">
                <dt className="text-muted-foreground">Город</dt>
                <dd className="font-medium">{client.city ?? client.region}</dd>
              </div>
            )}
          </dl>
          <p className="mt-2 text-xs text-muted-foreground">
            Меняется в CRM — здесь только показываем, чтобы не заводить второй
            источник правды.
          </p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="farm-name">Название хозяйства</Label>
          <Input
            id="farm-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            data-testid="farm-name-input"
          />
        </div>

        <div className="space-y-2">
          <Label>Отдел / филиал — можно изменить</Label>
          <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className="w-full justify-between font-normal"
                data-testid="farm-cluster-trigger"
                disabled={departments.length === 0}
              >
                <span className={cn(!selected && 'text-muted-foreground')}>
                  {selected?.name ??
                    (departments.length === 0
                      ? 'Отделов пока нет'
                      : 'Без отдела')}
                </span>
                <ChevronDown className="h-4 w-4 text-muted-foreground" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
              <div className="flex items-center gap-2 border-b px-2 py-2">
                <Search className="h-3.5 w-3.5 text-muted-foreground" />
                <input
                  className="w-full bg-transparent py-1 text-sm outline-none"
                  placeholder="поиск по отделам и филиалам"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  data-testid="farm-cluster-search"
                />
              </div>
              <div className="max-h-56 overflow-y-auto py-1">
                {filtered.length === 0 ? (
                  <p className="px-3 py-2 text-sm text-muted-foreground">
                    Ничего не нашли
                  </p>
                ) : (
                  filtered.map((d) => (
                    <button
                      key={d.id}
                      type="button"
                      className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-accent"
                      onClick={() => {
                        setClusterId(d.id);
                        setPickerOpen(false);
                      }}
                      data-testid={`farm-cluster-option-${d.id}`}
                    >
                      <Check
                        className={cn(
                          'h-3.5 w-3.5',
                          d.id === clusterId ? 'opacity-100' : 'opacity-0',
                        )}
                      />
                      <span>{d.name}</span>
                      {d.id === suggestedCluster && (
                        <span className="ml-auto text-xs text-muted-foreground">
                          по данным CRM
                        </span>
                      )}
                    </button>
                  ))
                )}
              </div>
            </PopoverContent>
          </Popover>
          <p className="text-xs text-muted-foreground">
            По умолчанию — отдел ответственного менеджера из CRM. Это тот же
            уровень, что кластер в структуре компании.
          </p>
        </div>

        {/* Обещаем ровно то, что произойдёт. Про «появится в выпадашке скоупа»
            здесь молчим сознательно: клиентские хозяйства попадают в общую
            выпадашку только с орг-грантом (MVP-3), и обещание, сбывающееся
            через релиз, хуже отсутствия обещания. */}
        <p
          className="rounded-md border border-dashed border-border p-3 text-sm text-muted-foreground"
          data-testid="farm-setup-hint"
        >
          {selected ? (
            <>
              Хозяйство закрепится за отделом:{' '}
              <b className="text-foreground">{selected.name}</b>. Появится в
              списке сразу.
            </>
          ) : (
            'Хозяйство появится в списке сразу — отдел можно проставить позже.'
          )}{' '}
          Кабинет клиента создастся автоматически, когда сохраните первое поле.
        </p>

        {error && (
          <p className="text-sm text-destructive" data-testid="farm-setup-error">
            {error}
          </p>
        )}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
          >
            Отмена
          </Button>
          <Button
            variant="outline"
            onClick={() => submit(false)}
            disabled={!canSubmit}
            data-testid="farm-setup-save"
          >
            Завести
          </Button>
          <Button
            onClick={() => submit(true)}
            disabled={!canSubmit}
            data-testid="farm-setup-save-and-fields"
          >
            Завести и добавить поля →
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
